import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../lib/auth.jsx";
import { supabase } from "../lib/supabase.js";
import { findOrCreateDraftCase, getCaseById, updateChildProfile } from "../lib/cases.js";
import { listNotifications } from "../lib/notifications.js";
import { fetchCreditBalance, consumeCreditAndSubmit } from "../lib/payments.js";
import {
  ScreenLogin,
  ScreenOnboarding,
  ScreenHome,
  ScreenConcern,
  ScreenUpload,
  ScreenPackages,
  ScreenStatus,
  ScreenReport,
  ScreenNotifications,
  ScreenSettings,
  ScreenPurchaseHistory,
  TabBar,
} from "./screens.jsx";

const TAB_STEPS = new Set(["home", "notifications", "settings", "status", "report"]);

function ShellLoading() {
  return (
    <div
      className="app-shell"
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div className="spinner" />
    </div>
  );
}

export default function ParentApp() {
  const { session, profile, loading: authLoading, signOut } = useAuth();

  const [children, setChildren] = useState(null);
  const [activeCase, setActiveCase] = useState(null);
  const [pastReports, setPastReports] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [creditBalance, setCreditBalance] = useState(0);

  const [currentCase, setCurrentCase] = useState(null);

  const [step, setStep] = useState("home");
  const [submittingCase, setSubmittingCase] = useState(false);
  const [toast, setToast] = useState(null);
  // Where the back arrow on ScreenPackages should return to. Set per-entry so
  // the user lands on whatever tab they came from (home / settings / etc).
  const [packagesReturnTo, setPackagesReturnTo] = useState("home");

  const showToast = (t) => {
    setToast(t);
    setTimeout(() => setToast(null), 2500);
  };
  const go = (s) => {
    window.scrollTo(0, 0);
    setStep(s);
  };
  const goToPackages = (returnTo = "home") => {
    setPackagesReturnTo(returnTo);
    go("packages");
  };
  // 홈 chip: 잔여 있으면 결제 내역, 없으면 곧장 패키지 화면.
  const handleChip = () => {
    if (creditBalance > 0) {
      setPackagesReturnTo("home");
      go("purchase-history");
    } else {
      goToPackages("home");
    }
  };

  const loadCreditBalance = useCallback(async () => {
    if (!session?.user?.id) {
      setCreditBalance(0);
      return;
    }
    try {
      const n = await fetchCreditBalance(session.user.id);
      setCreditBalance(n);
    } catch (err) {
      console.error("credit balance load failed:", err);
    }
  }, [session?.user?.id]);

  const loadNotifications = useCallback(async () => {
    if (!session?.user?.id) {
      setNotifications([]);
      return;
    }
    try {
      const list = await listNotifications({ userId: session.user.id });
      setNotifications(list);
    } catch (err) {
      console.error("notifications load failed:", err);
    }
  }, [session?.user?.id]);

  const loadData = useCallback(async () => {
    if (!session?.user?.id) {
      setChildren(null);
      setActiveCase(null);
      setPastReports([]);
      return;
    }

    const { data: kids } = await supabase
      .from("it_children")
      .select("id, name, birth_month, avatar_url")
      .eq("parent_id", session.user.id)
      .order("created_at");

    setChildren(kids ?? []);

    if (!kids || kids.length === 0) {
      setActiveCase(null);
      setPastReports([]);
      return;
    }

    const childIds = kids.map((k) => k.id);

    const { data: active } = await supabase
      .from("it_cases")
      .select("id, status, concern_tags, concern_memo, created_at, submitted_at, videos:it_videos(count)")
      .in("child_id", childIds)
      .not("status", "in", "(draft,done,cancelled)")
      .order("submitted_at", { ascending: false, nullsFirst: false })
      .limit(1);
    const activeRow = active?.[0];
    setActiveCase(
      activeRow
        ? { ...activeRow, video_count: activeRow.videos?.[0]?.count ?? 0 }
        : null
    );

    const { data: reports } = await supabase
      .from("it_reports")
      .select("case_id, finalized_at, viewed_at, case:it_cases!inner(id, concern_tags, child_id)")
      .in("case.child_id", childIds)
      .order("finalized_at", { ascending: false })
      .limit(10);

    setPastReports(
      (reports ?? []).map((r) => ({
        case_id: r.case_id,
        finalized_at: r.finalized_at,
        viewed_at: r.viewed_at,
        title: r.case?.concern_tags?.[0]
          ? `${r.case.concern_tags[0]} 관찰`
          : "분석 리포트",
      }))
    );
  }, [session?.user?.id]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadNotifications(); }, [loadNotifications]);
  useEffect(() => { loadCreditBalance(); }, [loadCreditBalance]);

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  const handleNewCase = async () => {
    if (!session?.user || !children?.[0]) return;
    if (creditBalance <= 0) {
      goToPackages("home");
      return;
    }
    try {
      const draft = await findOrCreateDraftCase({
        userId: session.user.id,
        childId: children[0].id,
        planTier: profile?.plan_tier ?? "trial",
      });
      setCurrentCase(draft);
      go("concern");
    } catch (err) {
      showToast("시작할 수 없어요: " + (err.message ?? "unknown"));
    }
  };

  // 패키지 모델: 결제 화면 대신 잔여 분석권을 차감하고 즉시 received로 전이.
  // 차감 실패(NO_CREDIT, 동시성)면 패키지 화면으로 유도.
  const handleConsumeAndSubmit = async () => {
    if (!session?.user || !currentCase?.id || submittingCase) return;
    setSubmittingCase(true);
    try {
      await consumeCreditAndSubmit({
        userId: session.user.id,
        caseId: currentCase.id,
      });
      const fresh = await getCaseById(currentCase.id);
      if (fresh) setCurrentCase(fresh);
      await loadData();
      await loadNotifications();
      await loadCreditBalance();
      go("status");
      showToast("접수됐어요. 분석이 시작됩니다.");
    } catch (err) {
      if (err?.code === "NO_CREDIT") {
        await loadCreditBalance();
        showToast("잔여 분석권이 없어요. 패키지를 충전해주세요.");
        goToPackages("home");
      } else {
        showToast("제출 실패: " + (err?.message ?? "unknown"));
      }
    } finally {
      setSubmittingCase(false);
    }
  };

  const handleOpenActive = () => {
    if (!activeCase) return;
    setCurrentCase(activeCase);
    go(activeCase.status === "done" ? "report" : "status");
  };

  // Shared "load a specific case + go to report" helper for past-report taps
  // and notification taps
  const openReportForCase = async (caseId) => {
    try {
      const c = await getCaseById(caseId);
      if (!c) {
        showToast("리포트를 찾지 못했어요");
        return;
      }
      setCurrentCase(c);
      go("report");
    } catch (err) {
      showToast("리포트 열기 실패: " + (err.message ?? "unknown"));
    }
  };

  const goHome = () => {
    setCurrentCase(null);
    loadData();
    loadNotifications();
    loadCreditBalance();
    go("home");
  };

  // ─── Render branches ────────────────────────────────────────────────
  if (authLoading) return <ShellLoading />;
  if (!session) {
    return (
      <div className="app-shell">
        <ScreenLogin />
      </div>
    );
  }
  if (children === null) return <ShellLoading />;
  if (children.length === 0) {
    return (
      <div className="app-shell">
        <ScreenOnboarding
          parentId={session.user.id}
          onDone={() => loadData()}
        />
      </div>
    );
  }

  const primaryChild = children[0];

  return (
    <div className="app-shell">
      {toast && <div className="toast">📖 {toast}</div>}

      {step === "home" && (
        <ScreenHome
          profile={profile}
          child={primaryChild}
          activeCase={activeCase}
          pastReports={pastReports}
          creditBalance={creditBalance}
          onNewCase={handleNewCase}
          onOpenCase={handleOpenActive}
          onOpenReport={openReportForCase}
          onBuyPackage={handleChip}
        />
      )}

      {step === "packages" && (
        <ScreenPackages
          profile={profile}
          session={session}
          creditBalance={creditBalance}
          onBack={() => go(packagesReturnTo)}
        />
      )}

      {step === "purchase-history" && (
        <ScreenPurchaseHistory
          session={session}
          creditBalance={creditBalance}
          onBack={() => go(packagesReturnTo)}
          onBuyPackage={() => goToPackages(packagesReturnTo)}
        />
      )}

      {step === "notifications" && (
        <ScreenNotifications
          notifications={notifications}
          userId={session.user.id}
          onOpenCase={async (caseId) => {
            await loadNotifications();
            await openReportForCase(caseId);
          }}
          onChange={loadNotifications}
        />
      )}

      {step === "settings" && (
        <ScreenSettings
          profile={profile}
          session={session}
          children={children}
          creditBalance={creditBalance}
          onBuyPackage={() => goToPackages("settings")}
          onOpenHistory={() => {
            setPackagesReturnTo("settings");
            go("purchase-history");
          }}
          onUpdateChild={async (childId, patch) => {
            try {
              await updateChildProfile({ childId, ...patch });
              await loadData();
              showToast("아이 정보를 저장했어요");
            } catch (err) {
              showToast("저장 실패: " + (err.message ?? "unknown"));
              throw err;
            }
          }}
          onSignOut={signOut}
        />
      )}

      {step === "concern" && currentCase && (
        <ScreenConcern
          caseRecord={currentCase}
          onCaseUpdate={setCurrentCase}
          onBack={goHome}
          onNext={() => go("upload")}
        />
      )}

      {step === "upload" && currentCase && (
        <ScreenUpload
          caseRecord={currentCase}
          userId={session.user.id}
          onBack={() => go("concern")}
          onNext={handleConsumeAndSubmit}
          submitting={submittingCase}
          creditBalance={creditBalance}
        />
      )}

      {step === "status" && currentCase && (
        <ScreenStatus
          caseRecord={currentCase}
          onCaseUpdate={setCurrentCase}
          onBack={goHome}
          onDone={() => go("report")}
        />
      )}

      {step === "report" && (
        <ScreenReport caseRecord={currentCase} onBack={goHome} />
      )}

      {TAB_STEPS.has(step) && (
        <TabBar
          active={step}
          unreadCount={unreadCount}
          onChange={(t) => {
            if (t === step) return;
            if (t === "notifications") loadNotifications();
            go(t);
          }}
        />
      )}
    </div>
  );
}
